(function () {
  'use strict';
  const selector = 'form[data-oss-search]';
  const states = new WeakMap();
  let nextId = 0;
  let active = null;

  function cancel(state) {
    clearTimeout(state.timer);
    clearTimeout(state.timeout);
    state.version += 1;
    if (state.controller) state.controller.abort();
    state.controller = null;
    state.form.removeAttribute('aria-busy');
  }

  function close(state) {
    cancel(state);
    state.popup.hidden = true;
    state.popup.replaceChildren();
    state.input.setAttribute('aria-expanded', 'false');
    state.input.removeAttribute('aria-activedescendant');
    state.status.textContent = '';
    state.index = -1;
    if (active === state) active = null;
  }

  function setup(form) {
    if (states.has(form)) return states.get(form);
    const input = form.querySelector('input[name="q"]');
    const popup = form.querySelector('[data-oss-results]');
    const status = form.querySelector('[data-oss-status]');
    if (!input || !popup || !status) return null;
    const id = 'oss-search-' + (++nextId);
    const state = { form, input, popup, status, id, version: 0, index: -1 };
    states.set(form, state);
    input.id = id;
    input.setAttribute('role', 'combobox');
    input.setAttribute('aria-autocomplete', 'list');
    input.setAttribute('aria-haspopup', 'listbox');
    input.setAttribute('aria-controls', id + '-list');
    input.setAttribute('autocomplete', 'off');
    close(state);
    return state;
  }

  function refresh() {
    if (active && !active.form.isConnected) close(active);
    document.querySelectorAll(selector).forEach(setup);
  }

  function stateFor(target) {
    const form = target.closest && target.closest(selector);
    return form ? setup(form) : null;
  }

  function current(state, version, term) {
    return state.version === version && state.form.isConnected &&
      state.input.value.trim() === term && state.form.contains(document.activeElement);
  }

  function fallback(state) {
    state.popup.replaceChildren();
    state.popup.hidden = true;
    state.input.setAttribute('aria-expanded', 'false');
    state.input.removeAttribute('aria-activedescendant');
    state.status.textContent = state.form.dataset.unavailable;
  }

  async function request(state, term) {
    const version = state.version;
    const controller = new AbortController();
    state.controller = controller;
    state.form.setAttribute('aria-busy', 'true');
    state.status.textContent = state.form.dataset.loading;
    const url = new URL(state.form.dataset.suggestUrl, window.location.origin);
    url.search = new URLSearchParams({
      q: term, 'resources[type]': 'product', 'resources[limit]': '6',
      section_id: 'oss-predictive-search'
    }).toString();
    state.timeout = setTimeout(() => controller.abort(), 8000);
    try {
      const response = await fetch(url.toString(), {
        signal: controller.signal, credentials: 'same-origin', headers: { Accept: 'text/html' }
      });
      if (!response.ok) throw new Error('Search unavailable');
      const html = await response.text();
      if (!current(state, version, term)) return;
      const result = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-oss-response]');
      if (!result || !result.querySelector('[role="listbox"]')) throw new Error('Invalid search response');
      const list = result.querySelector('[role="listbox"]');
      list.id = state.id + '-list';
      result.querySelectorAll('[role="option"]').forEach((option, index) => {
        option.id = state.id + '-option-' + index;
        option.setAttribute('aria-selected', 'false');
      });
      state.popup.replaceChildren(...result.childNodes);
      state.popup.hidden = false;
      state.input.setAttribute('aria-expanded', 'true');
      state.status.textContent = result.dataset.announcement;
      state.index = -1;
    } catch (error) {
      if (current(state, version, term)) fallback(state);
    } finally {
      if (state.version === version) {
        clearTimeout(state.timeout);
        state.controller = null;
        state.form.removeAttribute('aria-busy');
      }
    }
  }

  function search(state, composing) {
    if (active && active !== state) close(active);
    close(state);
    active = state;
    const term = state.input.value.trim();
    if (composing || term.length < 2) return;
    state.timer = setTimeout(() => request(state, term), 250);
  }

  document.addEventListener('input', event => {
    const state = stateFor(event.target);
    if (state && event.target === state.input) search(state, event.isComposing);
  });
  document.addEventListener('compositionstart', event => {
    const state = stateFor(event.target);
    if (state) close(state);
  });
  document.addEventListener('compositionend', event => {
    const state = stateFor(event.target);
    if (state && event.target === state.input) search(state, false);
  });
  document.addEventListener('focusin', event => {
    const state = stateFor(event.target);
    if (state && event.target === state.input) search(state, false);
    else if (active && (!state || state !== active)) close(active);
  });
  document.addEventListener('focusout', event => {
    const state = stateFor(event.target);
    if (state && !state.form.contains(event.relatedTarget)) close(state);
  });
  document.addEventListener('pointerdown', event => {
    if (active && !active.form.contains(event.target)) close(active);
  });
  document.addEventListener('submit', event => {
    const state = stateFor(event.target);
    if (state) close(state);
  });
  document.addEventListener('keydown', event => {
    const state = stateFor(event.target);
    if (!state || event.isComposing) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      close(state);
      return;
    }
    if (event.target !== state.input || state.popup.hidden) return;
    const options = Array.from(state.popup.querySelectorAll('[role="option"]'));
    if (event.key === 'Enter' && state.index >= 0) {
      event.preventDefault();
      options[state.index].click();
    } else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const step = event.key === 'ArrowDown' ? 1 : -1;
      state.index = state.index < 0 ? (step > 0 ? 0 : options.length - 1) :
        (state.index + step + options.length) % options.length;
      options.forEach((option, index) => option.setAttribute('aria-selected', String(index === state.index)));
      state.input.setAttribute('aria-activedescendant', options[state.index].id);
      options[state.index].scrollIntoView({ block: 'nearest' });
    }
  });
  window.OSSPredictiveSearch = { refresh };
  refresh();
})();
